import { useState } from "react";
import type * as React from "react";
import { Box, Typography } from "@mui/material";
import CloseIcon from "assets/images/Close.svg";
import GLButton from "components/v3/sharedComponents/GLButton";
import MemberFeedbackSurvey from "components/v3/sharedComponents/surveys/MemberFeedbackSurvey";
import ModalWrapper from "components/v2/sharedComponents/Modals/ModalComponents/ModalWrapper";
import { COLORS } from "components/v3/Theme/colors";
import Translate from "translate/Translate";

type MemberFeedbackSurveyModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
};

const modalContainerSx = {
  borderRadius: "24px !important",
  boxSizing: "border-box",
  flexBasis: "auto !important",
  marginBottom: { xs: "16px", sm: "32px" },
  marginLeft: { xs: "10px", sm: "30px" },
  marginRight: { xs: "10px", sm: "30px" },
  marginTop: { xs: "32px", sm: "40px" },
  maxWidth: "720px !important",
  overflow: "visible",
  position: "relative",
  padding: {
    xs: "32px 16px 24px 16px !important",
    md: "40px 32px 32px 32px !important"
  },
  outline: 0,
  width: {
    xs: "calc(100vw - 20px) !important",
    sm: "calc(100vw - 60px) !important"
  }
} as const;

const MemberFeedbackSurveyModal: React.FC<MemberFeedbackSurveyModalProps> = ({
  isOpen = false,
  onClose
}) => {
  const [showThankYou, setShowThankYou] = useState(false);

  const handleClose = () => {
    setShowThankYou(false);
    onClose?.();
  };

  return (
    <ModalWrapper
      closeOnBackdropClick={true}
      isOpen={isOpen}
      onClose={handleClose}
      modalContainerSx={modalContainerSx}
      sx={{
        alignItems: "flex-start",
        overflowY: "auto !important",
        "& .MuiModal-backdrop": {
          backgroundColor: `${COLORS.PRIMARY_DARKER}80`
        },
        outline: 0
      }}
    >
      <Box
        alt="Close modal"
        component="img"
        onClick={handleClose}
        src={CloseIcon}
        sx={{
          cursor: "pointer",
          position: "absolute",
          right: { sm: -20, xs: -8 },
          top: -24,
          zIndex: 2
        }}
      />
      {showThankYou ? (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            py: 2,
            textAlign: "center",
            width: "100%"
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxWidth: { md: "90%", xs: "100%" }
            }}
          >
            <Typography
              component="h2"
              variant="body_strong_lg_primary"
              sx={{ m: 0 }}
            >
              <Translate text="Thank you for your feedback!" />
            </Typography>
            <Typography component="p" variant="body_black" sx={{ m: 0 }}>
              <Translate text="We sincerely appreciate that you took time to share your thoughts. Your input helps us improve Genomic Life for everyone. Thank you!" />
            </Typography>
          </Box>
          <GLButton buttonType="primary" onClick={handleClose}>
            <Typography variant="body_strong_white">
              <Translate text="Close" />
            </Typography>
          </GLButton>
        </Box>
      ) : (
        <MemberFeedbackSurvey onComplete={() => setShowThankYou(true)} />
      )}
    </ModalWrapper>
  );
};

export default MemberFeedbackSurveyModal;
